/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { selectIsNewIa } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { ApiStatus } from '../../../../../ts';
import { Skill } from '../../../../../ts/skill';
import { CreateAsyncThunk, SkillsSlice } from '../../../../../ts/state';
import * as API from '../../../api/skills';
import { getErrorString } from '../../../util/errorHandler';
import { createdObjectEvents } from '../../../util/trackers/segment';
import { selectIsAccessUpdatesEnabled } from '../../features/selectors';
import {
  addErrorMessage,
  addMessage,
  ToastStatus
} from '../../messages/actions';
import { getAssistantId } from '../../session/selectors';
import { entityAdapter } from '../adapter';
import { VALIDATORS } from '../helper';

const COPY = ' copy';
const ELLIPSIS = '...';
const COPY_WITH_ELLIPSIS = ELLIPSIS + COPY;
const NAME_LENGTH = VALIDATORS.name.length - 1;
const DESC_LENGTH = VALIDATORS.description.length - 1;

const truncateIfNeeded = (str: string, maxLength: number) => {
  if ((str + COPY).length <= maxLength) {
    return str + COPY;
  }

  const length = maxLength - COPY_WITH_ELLIPSIS.length;
  return str.substring(0, Math.min(length, str.length)) + COPY_WITH_ELLIPSIS;
};

type DuplicateSkill = {
  skill_id: string;
  newName?: string;
  newDescription?: string;
};

/**
 * Duplicates an existing skill using a new name and description
 * @param {string} params The skill to duplicate
 *
 * @returns {AsyncThunk} The async action
 */
export const duplicateSkill = createAsyncThunk<
  Skill,
  DuplicateSkill,
  CreateAsyncThunk
>('skills/duplicate', async (params, { dispatch, getState }) => {
  try {
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    const { skill_id, newName, newDescription } = params;

    const exportedSkill = await API.getSkill(
      skill_id,
      useNewIAUpdatedAccess,
      assistantId,
      { export: true }
    );

    const name = newName ?? truncateIfNeeded(exportedSkill.name, NAME_LENGTH);

    const description =
      newDescription ??
      truncateIfNeeded(exportedSkill.description, DESC_LENGTH);

    const new_ia = selectIsNewIa(getState());

    const result = await API.createSkill(
      {
        ...exportedSkill,
        name,
        description
      },
      { new_ia }
    );

    if (!result.workspace_id) {
      result.workspace_id = result.skill_id;
    }

    dispatch(
      addMessage({
        message: `Copied ${exportedSkill.name}`,
        status: ToastStatus.SUCCESS
      })
    );
    // Notify Analytics
    createdObjectEvents.skill({
      object: result.skill_id,
      category: 'duplicated',
      resultValue: 'success',
      successFlag: true
    });

    return result;
  } catch (error) {
    dispatch(addErrorMessage(error));
    createdObjectEvents.skill({
      object: null, //there is no object in the failed case.
      category: 'duplicated',
      resultValue: getErrorString(error),
      successFlag: false
    });
    throw error;
  }
});

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(duplicateSkill.pending, (state, _action) => {
      state.duplicateStatus = ApiStatus.LOADING;
      state.duplicateError = null;
    })
    .addCase(duplicateSkill.fulfilled, (state, action) => {
      state.duplicateStatus = ApiStatus.SUCCEEDED;
      state.duplicateError = null;
      entityAdapter.addOne(state, action.payload);
    })
    .addCase(duplicateSkill.rejected, (state, action) => {
      state.duplicateStatus = ApiStatus.ERROR;
      state.duplicateError = action.error;
    });
};

export default reducer;
